import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Infinite auto-scrolling strip — drag with mouse, swipe on touch.
 * Fills the viewport then loops seamlessly.
 */
const DraggableMarquee = ({
  children,
  speed = 40,
  direction = "left",
  className = "",
  gradient = false,
  gradientColor = "white",
  gradientWidth = 64,
}) => {
  const viewportRef = useRef(null);
  const measureRef = useRef(null);
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const loopWidthRef = useRef(0);
  const rafRef = useRef(0);
  const lastTsRef = useRef(0);
  const draggingRef = useRef(false);
  const pointerIdRef = useRef(null);
  const lastXRef = useRef(0);
  const movedRef = useRef(false);
  const [paused, setPaused] = useState(false);
  const [setCount, setSetCount] = useState(2);

  const dirMul = direction === "right" ? 1 : -1;
  const items = useMemo(() => React.Children.toArray(children), [children]);

  const normalize = useCallback((value) => {
    const w = loopWidthRef.current;
    if (w <= 0) return value;
    let next = value % w;
    if (next > 0) next -= w;
    if (next < -w) next += w;
    return next;
  }, []);

  const applyTransform = useCallback(() => {
    if (!trackRef.current) return;
    trackRef.current.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
  }, []);

  const remesure = useCallback(() => {
    const viewport = viewportRef.current;
    const measure = measureRef.current;
    const track = trackRef.current;
    if (!viewport || !measure || !track) return;

    const oneSet = measure.offsetWidth;
    const vw = viewport.offsetWidth;
    if (oneSet > 0 && vw > 0) {
      const need = Math.max(2, Math.ceil(vw / oneSet) + 1);
      if (need !== setCount) {
        setSetCount(need);
        return;
      }
    }

    // Loop width = first meta-group (half of track)
    const firstGroup = track.children[0];
    loopWidthRef.current = firstGroup?.offsetWidth || 0;
    offsetRef.current = normalize(offsetRef.current);
    applyTransform();
  }, [applyTransform, normalize, setCount]);

  useEffect(() => {
    remesure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(remesure) : null;
    if (ro) {
      if (viewportRef.current) ro.observe(viewportRef.current);
      if (measureRef.current) ro.observe(measureRef.current);
      if (trackRef.current) ro.observe(trackRef.current);
    }
    window.addEventListener("resize", remesure);
    const imgs = [
      ...(measureRef.current?.querySelectorAll("img") || []),
      ...(trackRef.current?.querySelectorAll("img") || []),
    ];
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", remesure, { once: true });
    });
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", remesure);
    };
  }, [remesure, items, setCount]);

  useEffect(() => {
    const tick = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = Math.min(32, ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      if (!draggingRef.current && !paused && loopWidthRef.current > 0) {
        offsetRef.current = normalize(offsetRef.current + dirMul * speed * dt);
        applyTransform();
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lastTsRef.current = 0;
    };
  }, [applyTransform, dirMul, normalize, paused, speed]);

  const onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    draggingRef.current = true;
    movedRef.current = false;
    pointerIdRef.current = e.pointerId;
    lastXRef.current = e.clientX;
    setPaused(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current || pointerIdRef.current !== e.pointerId) return;
    const dx = e.clientX - lastXRef.current;
    if (Math.abs(dx) > 2) movedRef.current = true;
    lastXRef.current = e.clientX;
    offsetRef.current = normalize(offsetRef.current + dx);
    applyTransform();
  };

  const endDrag = (e) => {
    if (!draggingRef.current) return;
    if (e?.pointerId != null && pointerIdRef.current !== e.pointerId) return;
    draggingRef.current = false;
    pointerIdRef.current = null;
    setPaused(false);
  };

  const onClickCapture = (e) => {
    if (movedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      movedRef.current = false;
    }
  };

  const renderSet = (keyPrefix) =>
    items.map((child, i) => (
      <React.Fragment key={`${keyPrefix}-${i}`}>{child}</React.Fragment>
    ));

  const metaGroup = (keyPrefix) => (
    <div className="flex shrink-0">
      {Array.from({ length: setCount }, (_, i) => (
        <React.Fragment key={`${keyPrefix}-set-${i}`}>{renderSet(`${keyPrefix}-${i}`)}</React.Fragment>
      ))}
    </div>
  );

  const fadeStyle = (side) =>
    gradient
      ? {
          background: `linear-gradient(${side === "left" ? "90deg" : "270deg"}, ${gradientColor}, transparent)`,
          width: typeof gradientWidth === "number" ? `${gradientWidth}px` : gradientWidth,
        }
      : null;

  return (
    <div
      className={`relative overflow-hidden select-none touch-pan-y ${className}`}
      ref={viewportRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => {
        if (!draggingRef.current) setPaused(false);
      }}
    >
      {/* Hidden single set for width measurement */}
      <div
        ref={measureRef}
        className="flex w-max absolute opacity-0 pointer-events-none -z-10"
        aria-hidden="true"
      >
        {renderSet("m")}
      </div>

      <div
        ref={trackRef}
        className="flex w-max will-change-transform cursor-grab active:cursor-grabbing"
        style={{ transform: "translate3d(0,0,0)" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
        role="list"
        aria-label="Geser untuk melihat lebih banyak"
      >
        {metaGroup("a")}
        <div className="flex shrink-0" aria-hidden="true">
          {Array.from({ length: setCount }, (_, i) => (
            <React.Fragment key={`b-set-${i}`}>{renderSet(`b-${i}`)}</React.Fragment>
          ))}
        </div>
      </div>

      {gradient && (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 z-[1]" style={fadeStyle("left")} />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-[1]" style={fadeStyle("right")} />
        </>
      )}
    </div>
  );
};

export default DraggableMarquee;
